<template>
    <div class="unit-card">
        <card
            v-if="unitType === UNIT_TYPE_ARCHER"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="times-circle"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'opponent', position: '1,3', icon: 'times' },
                { type: 'opponent', position: '1,4' },
                { type: 'opponent', position: '3,2' },
            ]"
            :arrows="[
                { type: 'attack', angle: '90', position: '1,3' },
                { type: 'attack', angle: '211-alt', position: '2,2' },
            ]"
            :lines="[
                { type: 'attack', position: '1,2', angle: '90' },
                { type: 'attack', position: '1,2', angle: '150' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					<strong>Attack</strong> a unit two spaces away. The intervening space may be occupied by a unit.
				</span>
			</template>
            <template v-slot:description>
				<span>
				The Archer can only <strong>attack</strong> by using its <strong>tactic</strong>.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_BERSERKER"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-length="long-description"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,3' },
                { type: 'self', position: '1,3', icon: 'times'},
                { type: 'opponent', position: '1,4' },
                { type: 'opponent', position: '2,3' },
            ]"
            :arrows="[
                { type: 'attack', angle: '90', position: '1,3' },
                { type: 'attack', angle: '150', position: '1,3' },
            ]"
        >
            <template v-slot:description>
				<span>
					After the Berserker <strong>maneuvers</strong>, you may <strong>maneuver</strong>
					it again by discarding a <strong>bolstered</strong> coin from the Berserker unit.
					You may do this multiple times, but you may not remove the final coin.
				</span>
			</template>
        </card>
		<card
            v-if="unitType === UNIT_TYPE_BERSERKER_V2"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-length="long-description"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,3' },
                { type: 'self', position: '1,3', icon: 'times'},
                { type: 'opponent', position: '1,4' },
                { type: 'opponent', position: '2,3' },
            ]"
            :arrows="[
                { type: 'attack', angle: '90', position: '1,3' },
                { type: 'attack', angle: '150', position: '1,3' },
            ]"
			version="v2"
        >
            <template v-slot:description>
				<span>
					After the Berserker <strong>attacks</strong> or <strong>moves</strong>, it may
					<strong>attack</strong> or <strong>move</strong> again
					by discarding a <strong>bolstered</strong> coin from the Berserker unit.
					You may do this multiple times, but you may not remove the final coin.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_CAVALRY"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'opponent', position: '2,3' },
            ]"
            :arrows="[
                { type: 'move', angle: '150', position: '1,2' },
                { type: 'attack', angle: '90', position: '2,2' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					<strong>Move</strong> and then <strong>attack</strong>.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_CROSSBOWMAN"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'opponent', position: '1,3' },
                { type: 'opponent', position: '1,4', icon: 'times' },
                { type: 'opponent', position: '3,3' },
            ]"
            :arrows="[
                { type: 'attack', position: '2,2', angle: '150-alt' },
            ]"
            :lines="[
                { type: 'attack', position: '1,2', angle: '150' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					<strong>Attack</strong> a unit two spaces away in a straight line.
					The intervening space cannot be occupied by a unit.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_ENSIGN"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'friendly', position: '2,3' },
            ]"
            :colored-tiles="['1,2', '2,2', '2,3']"
            :arrows="[
                { type: 'move', angle: '211', position: '2,3' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					Choose a friendly unit within two spaces of the Ensign.
                     The chosen unit performs a normal <strong>move</strong> to a space
                     within two spaces of the Ensign.
            	</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_FOOTMAN"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'self', position: '1,4' },
                { type: 'opponent', position: '2,2' },
            ]"
            :arrows="[
                { type: 'move', angle: '32', position: '1,4' },
                { type: 'attack', angle: '150', position: '1,2' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					<span>Perform one <strong>maneuver</strong> with each Footman unit on the board.</span>
				</span>
			</template>
            <template v-slot:description>
				<span>
					<span>Two Footman units may be <strong>deployed</strong> at a time.</span>
				</span>
			</template>
        </card>
		<card
            v-if="unitType === UNIT_TYPE_FOOTMAN_V2"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
			description-length="long-description"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'self', position: '1,4' },
                { type: 'opponent', position: '2,2' },
            ]"
            :arrows="[
                { type: 'move', angle: '32', position: '1,4' },
                { type: 'attack', angle: '150', position: '1,2' },
            ]"
			version="v2"
        >
            <template v-slot:tactic>
				<span>
					<span>Perform one <strong>maneuver</strong> with each Footman unit on the board.</span>
				</span>
			</template>
            <template v-slot:description>
				<span>
					After you <strong>recruit</strong> a Footman, you may <strong>deploy</strong> it immediately if you already have a Footman unit deployed.
					<span>Two Footman units may be <strong>deployed</strong> at a time.</span>
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_KNIGHT"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'opponent', position: '1,3' },
                { type: 'opponent', position: '3,3' },
                { type: 'opponent', position: '3,3' },
            ]"
            :lines="[
                { type: 'attack', position: '3,3', angle: '328' },
            ]"
            :arrows="[
                { type: 'attack', angle: '328', position: '2,2' },
            ]"
        >
            <template v-slot:description>
				<span>
					The knight can only be <strong>attacked</strong> by units that are <strong>bolstered</strong>.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_LANCER"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="times-circle"
            :mini-units="[
                { type: 'self', position: '2,1' },
                { type: 'opponent', position: '0,2' },
                { type: 'opponent', position: '2,4' },
            ]"
            :arrows="[
                { type: 'move', angle: '90', position: '2,2' },
                { type: 'attack', angle: '90', position: '2,3' },
                { type: 'move', angle: '32', position: '2,1' },
                { type: 'attack', angle: '32', position: '1,2' },
            ]"
            :lines="[
                { type: 'move', position: '2,1', angle: '90' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					<strong>Move</strong> one or two spaces and then <strong>attack</strong>, all in a straight line.
				</span>
			</template>
            <template v-slot:description>
				<span>
					The Lancer can only <strong>attack</strong> by using its <strong>tactic</strong>.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_LIGHT_CAVALRY"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            :mini-units="[
                { type: 'self', position: '1,3' },
            ]"
            :arrows="[
                { type: 'move', angle: '150', position: '1,3' },
                { type: 'move', angle: '90', position: '2,3' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					<strong>Move</strong> two spaces.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_MARSHAL"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'friendly', position: '2,3' },
                { type: 'opponent', position: '2,4' },
            ]"
            :colored-tiles="['1,2', '2,2', '2,3']"
            :arrows="[
                { type: 'attack', angle: '90', position: '2,3' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					Choose a friendly unit that is within two spaces of the Marshal.
					The chosen unit <strong>attacks</strong>, if able.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_MERCENARY"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,3' },
            ]"
            :arrows="[
                { type: 'move', angle: '90', position: '1,3' },
            ]"
        >
            <template v-slot:description>
				<span>
					After you <strong>recruit</strong> a Mercenary, you may <strong>maneuver</strong> your Mercenary unit.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_PIKEMAN"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,3' },
                { type: 'opponent', position: '2,3', icon: 'times' },
            ]"
            :arrows="[
                { type: 'attack', angle: '328', position: '2,3' },
            ]"
        >
            <template v-slot:description>
				<span>
                	When the Pikeman is <strong>attacked</strong> by an adjacent unit, remove a coin from that unit.
            	</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_ROYAL_GUARD"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-length="long-description"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '2,2' },
            ]"
            :arrows="[
                { type: 'move', angle: '90', position: '2,2' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					Discard the Royal Coin to <strong>move</strong> the Royal Guard.
				</span>
			</template>
            <template v-slot:description>
				<span>
					When the Royal Guard is <strong>attacked</strong>, you may remove a Royal Guard
					coin from the supply rather than from its unit.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_ROYAL_GUARD_NOBILITY"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-length="long-description"
            tactic-length="long-description"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '2,2' },
            ]"
            :arrows="[
                { type: 'move', angle: '90', position: '2,3' },
            ]"
            :lines="[
                { type: 'move', position: '2,2', angle: '90' },
            ]"
            :bases="[
                { position: '2,4' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					Discard the Royal Coin to <strong>move</strong> the Royal Guard
					up to 2 spaces to a location that you control.
				</span>
			</template>
            <template v-slot:description>
				<span>
					When the Royal Guard is <strong>attacked</strong>, you may remove a Royal Guard
					coin from the supply rather than from its unit.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_SCOUT"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '2,2' },
                { type: 'friendly', position: '2,3' },
            ]"
            :colored-tiles="['1,3', '1,4', '2,2', '2,3', '2,4', '3,3', '3,4']"
        >
            <template v-slot:description>
				<span>
					The Scout may be <strong>deployed</strong> adjacent to any friendly unit.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_SWORDSMAN"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '2,2' },
                { type: 'opponent', position: '2,3' },
            ]"
            :arrows="[
                { type: 'attack', angle: '90', position: '2,2' },
                { type: 'move', angle: '150', position: '2,2' },
            ]"
        >
            <template v-slot:description>
				<span>
					After the Swordsman <strong>attacks</strong>, it may <strong>move</strong>.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_WARRIOR_PRIEST"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-length="medium-description"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,3' },
                { type: 'opponent', position: '1,4' },
            ]"
            :arrows="[
                { type: 'attack', angle: '90', position: '1,3' },
            ]"
        >
            <template v-slot:description>
				<span>
					After the Warrior Priest <strong>attacks</strong> or <strong>controls</strong>,
					draw one coin from your bag and immediately use it to take any <strong>action</strong>.
				</span>
			</template>
        </card>
		<card
            v-if="unitType === UNIT_TYPE_WARRIOR_PRIEST_V2"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-length="medium-description"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,3' },
                { type: 'opponent', position: '1,4' },
            ]"
            :arrows="[
                { type: 'attack', angle: '90', position: '1,3' },
            ]"
			version="v2"
        >
            <template v-slot:description>
				<span>
					After the Warrior Priest <strong>attacks</strong> or <strong>controls</strong>,
					draw one coin from your bag and immediately use it to take any <strong>action</strong>.
					You may only use this attribute once per turn.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_BANNERMAN"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,3' },
                { type: 'opponent', position: '2,3' },
            ]"
            :colored-tiles="['0,2', '0,3', '1,2', '1,4', '2,2', '2,3']"
            :arrows="[
                { type: 'move', angle: '211', position: '2,3' },
            ]"
        >
			<template v-slot:description>
				<span>
					After the Bannerman <strong>maneuvers</strong> you may move one adjacent enemy unit one space.
				</span>
			</template>
        </card>
         <card
            v-if="unitType === UNIT_TYPE_BISHOP"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '2,3', icon: 'times' },
                { type: 'opponent', position: '1,3' },
                { type: 'opponent', position: '1,3' },
            ]"
            :arrows="[
                { type: 'attack', angle: '150', position: '1,3' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					<strong>Recruit</strong> and then either <strong>move</strong> or <strong>attack</strong>.
				</span>
			</template>
            <template v-slot:description>
				<span>
					The Bishop cannot be <strong>attacked</strong> by units that are <strong>bolstered</strong>.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_HERALD"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,4' },
                { type: 'friendly', position: '2,4' },
            ]"
            :colored-tiles="['0,3', '0,4', '1,3', '1,5', '2,3', '2,4']"
            :proclamation-ribbon="true"
        >
            <template v-slot:tactic>
				<span>
					<strong>Bolster</strong> one adjacent un-bolstered
					friendly unit with a coin from the supply.
				</span>
			</template>
            <template v-slot:description>
				<span>
					After you <strong>proclaim</strong>, the Herald may <strong>maneuver</strong>.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_EARL"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '2,3' },
            ]"
            :arrows="[
                { type: 'move', angle: '32', position: '2,3' },
            ]"
            :proclamation-ribbon="true"
        >
            <template v-slot:tactic>
				<span>
					<strong>Control</strong> a location and then <strong>proclaim</strong> without placing a Seal, even
					if the Decree already has your seal.
				</span>
			</template>
            <template v-slot:description>
				<span>
					After the Earl is <strong>deployed</strong> it may <strong>move</strong>.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_SAPPER"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            description-length="long-description"
            tactic-length="long-description"
            :mini-units="[
                { type: 'self', position: '2,3' },
            ]"
            :arrows="[
                { type: 'move', angle: '32', position: '2,3' },
                { type: 'attack', angle: '32', position: '1,4', customClass: 'sapper' },
            ]"
            :forts="[
                { position: '0,4' }
            ]"
        >
            <template v-slot:tactic>
				<span>
					<strong>Move</strong> and then <strong>attack</strong> a Fortification
				</span>
			</template>
            <template v-slot:description>
				<span>
					When the Sapper <strong>moves</strong> to a location without a Fortification,
					you may place a Fortification from the supply on the location.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_SIEGE_TOWER"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            tactic-type="siege"
            :mini-units="[
                { type: 'self', position: '2,3' },
                { type: 'opponent', position: '2,2' },
                { type: 'opponent', position: '1,4' },
            ]"
            :arrows="[
                { type: 'attack', angle: '32', position: '2,3' },
                { type: 'attack', angle: '270', position: '2,3' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					<strong>Attack</strong> twice
				</span>
			</template>
            <template v-slot:description>
				<span>
					When you <strong>deploy</strong> the Siege Tower,
					you may immediately <strong>bolster</strong> it
					with a coin from the supply.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_TREBUCHET"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="times-circle"
            tactic-length="medium-description"
            tactic-type="siege"
            :mini-units="[
                { type: 'self', position: '2,3' },
                { type: 'opponent', position: '2,0' },
                { type: 'opponent', position: '0,4' },
            ]"
            :lines="[
                { type: 'attack', angle: '270', position: '2,2' },
                { type: 'attack', angle: '270', position: '2,1', customClass: 'trebuchet'},
                { type: 'attack', angle: '32', position: '2,3', customClass: 'trebuchet' },
            ]"
            :arrows="[
                { type: 'attack', angle: '270', position: '2,1' },
                { type: 'attack', angle: '32', position: '1,4' },
            ]"
        >
            <template v-slot:tactic>
				<span>
					<strong>Attack</strong> a unit two or three spaces away in a
					straight line. The intervening spaces may be
					occupied by a unit.
				</span>
			</template>
            <template v-slot:description>
				<span>
					The trebuchet can only <strong>attack</strong>
					by using its <strong>tactic</strong>.
				</span>
			</template>
        </card>
		<card
            v-if="unitType === UNIT_TYPE_TREBUCHET_V2"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="times-circle"
            tactic-length="medium-description"
            tactic-type="siege"
            :mini-units="[
                { type: 'self', position: '2,3' },
                { type: 'opponent', position: '2,0' },
                { type: 'opponent', position: '0,4' },
            ]"
            :lines="[
                { type: 'attack', angle: '270', position: '2,2' },
                { type: 'attack', angle: '270', position: '2,1', customClass: 'trebuchet'},
                { type: 'attack', angle: '32', position: '2,3', customClass: 'trebuchet' },
            ]"
            :arrows="[
                { type: 'attack', angle: '270', position: '2,1' },
                { type: 'attack', angle: '32', position: '1,4' },
            ]"
			version="v2"
        >
            <template v-slot:tactic>
				<span>
					<strong>Attack</strong> a unit three spaces away
					 in a straight line or a unit two spaces away.
					 The intervening spaces may be occupied by a unit.
				</span>
			</template>
            <template v-slot:description>
				<span>
					The trebuchet can only <strong>attack</strong>
					by using its <strong>tactic</strong>.
				</span>
			</template>
        </card>
		<card
            v-if="unitType === UNIT_TYPE_TREBUCHET_V3"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="times-circle"
            tactic-length="medium-description"
            tactic-type="siege"
            :mini-units="[
                { type: 'self', position: '2,3' },
                { type: 'opponent', position: '2,0' },
                { type: 'opponent', position: '0,4' },
            ]"
            :lines="[
                { type: 'attack', angle: '270', position: '2,2' },
                { type: 'attack', angle: '270', position: '2,1', customClass: 'trebuchet'},
                { type: 'attack', angle: '32', position: '2,3', customClass: 'trebuchet' },
            ]"
            :arrows="[
                { type: 'attack', angle: '270', position: '2,1' },
                { type: 'attack', angle: '32', position: '1,4' },
            ]"
			version="v2"
        >
            <template v-slot:tactic>
				<span>
					<strong>Attack</strong> a unit twice that is two or three spaces away in a straight line.
					 The intervening spaces may be occupied.
				</span>
			</template>
            <template v-slot:description>
				<span>
					The trebuchet can only <strong>attack</strong>
					by using its <strong>tactic</strong>.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_WAR_DRUMMER"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            description-length="medium-description"
            :mini-units="[
                { type: 'self', position: '1,3' },
                { type: 'friendly', position: '3,5', icon: 'coins' },
            ]"
            :arrows="[
                { type: 'move', angle: '90', position: '1,3' },
            ]"
        >
            <template v-slot:description>
				<span>
					After the War Drummer <strong>moves</strong>, you may choose a coin in a supply and put it on top of its owner’s bag.
					This is the next coin they will draw.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_WAR_WAGON"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            description-length="long-description"
            tactic-length="long-description"
            tactic-type="siege"
            :mini-units="[
                { type: 'friendly', position: '1,3' },
                { type: 'self', position: '2,2' },
            ]"
            :arrows="[
                { type: 'move', angle: '90', position: '1,3' },
                { type: 'move', angle: '32', position: '2,2' },
            ]"
            :colored-tiles="['1,3']"
        >
            <template v-slot:tactic>
				<span>
					<strong>Move</strong> an adjacent friendly unit and then
					<strong>move</strong> the War Wagon into the vacated space.
				</span>
			</template>
            <template v-slot:description>
				<span>
					When an adjacent friendly unit is <strong>attacked</strong>,
					you may remove a coin from the War Wagon unit rather
					than from the attacked unit.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_ASSASSIN"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            description-length="long-description"
            :mini-units="[
                 { type: 'self', position: '2,2' },
                 { type: 'opponent', position: '1,4' },
            ]"
            :arrows="[
                { type: 'move', angle: '32', position: '2,2' },
            ]"
            :mini-poison-tokens="[
                {type: 'poison-token', position: '1,4'},
            ]"
            :lines="[]"
            :poison-tokens-count="1"
        >
            <template v-slot:tactic>
				<span>
					<strong>Move</strong> and then <strong>poison</strong> an adjacent unit
				</span>
			</template>
            <template v-slot:description>
				<span>
					After the Assassin <strong>attacks</strong> a <strong>poisoned</strong> unit, you may take a coin of
					the <strong>attacked</strong> unit from the supply and remove it
					from the game.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_SABOTEUR"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            description-length="long-description"
            :mini-units="[
                { type: 'self', position: '2,2' },
                { type: 'friendly', position: '1,3' },
                { type: 'opponent', position: '0,3' },
                { type: 'opponent', position: '2,1' },
            ]"
            :arrows="[]"
            :bases="[]"
            :mini-poison-tokens="[
                {type: 'poison-token', position: '0,3'},
                {type: 'poison-token', position: '2,1'}
            ]"
            :poison-tokens-count="1"
        >
            <template v-slot:tactic>
				<span>
                	<strong>Poison</strong> a unit one or two spaces away. If two spaces,
					the intervening space may be occupied by a unit.
				</span>
			</template>
            <template v-slot:description>
				<span>
					After you <strong>recruit</strong> a Saboteur, you may use
					the Saboteur's <strong>tactic</strong>.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_INFILTRATOR"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            description-length="long-description"
            :mini-units="[
                { type: 'self', position: '2,2' },
            ]"
            :arrows="[
                { type: 'move', angle: '32', position: '2,2' },
            ]"
            :bases="[
                { position: '1,3' },
            ]"
            :lines="[]"
            :decoy-coin-type="UNIT_TYPE_INFILTRATOR"
        >
            <template v-slot:tactic>
				<span>
					<strong>Move</strong> ending in a location <strong>controlled</strong> by an opponent
					and then <strong>control</strong> that location.
				</span>
			</template>
            <template v-slot:description>
				<span>
					After the infiltrator <strong>controls</strong>, you may <strong>deceive</strong>
					your opponent.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_SKIRMISHER"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-length="long-description"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '2,2' },
                { type: 'opponent', position: '0,3' },
                { type: 'opponent', position: '3,5' },
            ]"
            :arrows="[
                { type: 'move', angle: '32', position: '2,2' },
                { type: 'move', angle: '90', position: '3,3' },
            ]"
            :lines="[
                { type: 'move', angle: '150', position: '2,2' },
                { type: 'move', angle: '90', position: '3,3', customClass: 'skirmisher' },
            ]"
            :decoy-coin-type="UNIT_TYPE_SKIRMISHER"
        >
            <template v-slot:tactic>
				<span>
					&nbsp;<strong>Move</strong> two spaces, ending in a space
					adjacent to an enemy unit.
				</span>
			</template>
            <template v-slot:description>
			<span>
                When the Skirmisher is attacked, you may <strong>deceive</strong>
                 your opponent. If you do, the attack does not remove a coin.
            </span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_ALCHEMIST"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-length="long-description"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'friendly', position: '1,4' },
                { type: 'opponent', position: '2,4' },
            ]"
            :arrows="[
                { type: 'move', angle: '90', position: '1,3' },
                { type: 'move', angle: '270', position: '1,3' },
                { type: 'attack', angle: '150', position: '1,4' },
            ]"
            :lines="[
                { type: 'move', position: '1,2', angle: '90' },
            ]"
            art-attribution="Bean Soup"
			:concept-attribution="['Valentin Arnaud,', 'SpruceGoose']"
        >
            <template v-slot:tactic>
				<span>
					Swap places with a friendly unit within two spaces of the Alchemist. The Alchemist may then
                	<strong>move</strong>, <strong>attack</strong>, or <strong>control</strong>.
            	</span>
			</template>
        </card>
        <div class="eliminated-overlay animated fade-in" v-if="eliminated">
            <fa-icon class="icon eliminated-icon" icon="ban" />
        </div>
        <card
            v-if="unitType === UNIT_TYPE_OVERLORD"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-length="long-description"
            description-icon="exclamation-circle"
            tactic-length="long-description"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'self', position: '1,2', icon: 'plus' },
                { type: 'opponent', position: '1,3' },
            ]"
            :arrows="[
                { type: 'attack', angle: '90', position: '1,2' },
            ]"
            :lines="[]"
            art-attribution="Bean Soup"
            concept-attribution="NoWaht"
        >
            <template v-slot:tactic>
				<span>
					After the Overlord <strong>attacks</strong>, it may <strong>bolster</strong> by placing
					the eliminated enemy coin facedown below it, not to exceed 5 coins.
				</span>
			</template>
            <template v-slot:description>
			<span>
                When the Overlord is <strong>attacked</strong>, remove coins from the bottom first.
            </span>
		</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_APPRENTICE"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            description-length="medium-description"
            :colored-tiles="['2,2', '2,3']"
            :mini-units="[
                { type: 'self', position: '2,3' },
                { type: 'friendly', position: '2,2' },
            ]"
            :arrows="[]"
            :lines="[]"
            art-attribution="Smashicons - Flaticon"
            :concept-attribution="['Valentin Arnaud,', 'SpruceGoose']"
			card-footer-class="condensed"
        >
            <template v-slot:tactic>
				<span>
					Copy the <strong>attribute</strong> and/or <strong>tactic</strong> of a friendly adjacent unit,
					then maneuver as that unit.
				</span>
			</template>
			<template v-slot:description>
				<span>
					You may discard the Royal Coin to move the Apprentice one space, ending adjacent to a friendly unit.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_EMISSARY"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            description-length="long-description"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'opponent', position: '2,3' },
                { type: 'opponent', position: '2,4' },
            ]"
            :colored-tiles="['1,2', '2,2', '2,3']"
            :arrows="[
                { type: 'attack', angle: '90', position: '2,3' },
            ]"
            :lines="[]"
            art-attribution="Flaticon"
            concept-attribution="A.K."

        >
            <template v-slot:tactic>
				<span >
					Choose a unit within two spaces of the Emissary.
					If it's an enemy, it <strong>attacks</strong> another enemy unit.
					If it's friendly, it <strong>controls</strong> a location.
				</span>
			</template>
        </card>
		<card
            v-if="unitType === UNIT_TYPE_EMISSARY_V2"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            description-length="long-description"
            :mini-units="[
                { type: 'self', position: '2,2' },
				{ type: 'friendly', position: '3,5', icon: 'crown' },
            ]"
			:bases="[]"
            :colored-tiles="['2,2']"
            :arrows="[
                { type: 'attack', angle: '32', position: '2,2' },
            ]"
            :lines="[]"
            :concept-attribution="['Valentin Arnaud,', 'SpruceGoose']"
			version="v2"
        >
			<template v-slot:tactic>
				<span >
					<strong>Control</strong>, then claim or give the initiative if it has not been claimed or given this round.
				</span>
			</template>
            <template v-slot:description>
				<span>
					When a player claims the initiative, you may <strong>manuever</strong> the Emissary.
				</span>
			</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_WARLORD"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            description-length="medium-description"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'friendly', position: '2,3', icon: 'spider' },
            ]"
            :colored-tiles="['1,2', '2,2', '2,3']"
            :arrows="[]"
        >
            <template v-slot:tactic>
<span >
                Choose a friendly unit within two spaces of the Warlord.
                 <strong>Maneuver</strong> the chosen unit then <strong>shock</strong> it.
            </span>
</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_PITCH_THROWER"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            description-length="long-description"
            tactic-length="long-description"
            :mini-units="[
                { type: 'self', position: '2,2' },
                { type: 'opponent', position: '1,4', icon: 'spider' },
            ]"
            :colored-tiles="['1,2', '2,2', '2,3']"
            :arrows="[]"
        >
            <template v-slot:tactic>
<span >
                Discard a bolstered coin from the Pitch Thrower and then <strong>shock</strong>
                 an enemy unit two spaces away. The intervening space may be occupied by a unit.
            </span>
</template>
            <template v-slot:description>
<span >
                When you <strong>recruit</strong> the Pitch Thrower you may <strong>bolster</strong> it with the coin you recruited.
            </span>
</template>
        </card>
        <div class="eliminated-overlay animated fade-in" v-if="eliminated">
            <fa-icon class="icon eliminated-icon" icon="ban" />
        </div>
        <card
            v-if="unitType === UNIT_TYPE_REARGUARD"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '2,1' },
                { type: 'opponent', position: '2,3' },
                { type: 'friendly', position: '2,4' },
            ]"
            :colored-tiles="['1,2', '2,2', '2,3']"
            :arrows="[
                { type: 'move', angle: '32', position: '2,1' },
                { type: 'attack', angle: '90', position: '2,3' },
            ]"
        >
            <template v-slot:description>
<span >
                After another friendly unit is <strong>attacked</strong>, the Rearguard may <strong>move</strong>.
            </span>
</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_RAIDER"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :bases="[
                { position: '1,4' },
            ]"
            :mini-units="[
                { type: 'self', position: '1,3' },
                { type: 'self', position: '1,3', icon: 'times' },
            ]"
            :arrows="[
                { type: 'move', angle: '90', position: '1,3' },
            ]"
        >
            <template v-slot:tactic>
<span >
                Discard a bolstered coin to <strong>move</strong> and then <strong>control</strong>.
            </span>
</template>
            <template v-slot:description>
<span >
                After the Raider is <strong>bolstered</strong> it may <strong>move</strong>.
            </span>
</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_HEAVY_CAVALRY"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '2,1' },
                { type: 'opponent', position: '2,3', icon: 'spider' },
            ]"
            :arrows="[
                { type: 'move', angle: '90', position: '2,1' },
                { type: 'move', angle: '32', position: '1,2' },
            ]"
            :lines="[
                { type: 'move', angle: '32', position: '2,1' },
            ]"
        >
            <template v-slot:tactic>
<span >
                <strong>Move</strong> 2 spaces in a straight line. The Heavy Cavalry may end this movement
                on a space with an enemy unit. <strong>Shock</strong> that unit.
            </span>
</template>
        </card>
        <card
            v-if="unitType === UNIT_TYPE_VANGUARD"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,2' },
                { type: 'opponent', position: '1,3', icon: 'spider' },
            ]"
            :colored-tiles="[]"
            :arrows="[]"
            :lines="[]"
        >
            <template v-slot:tactic>
<span >
                <strong>Shock</strong> an adjacent enemy unit.
            </span>
</template>
            <template v-slot:description>
<span >
                When a Vanguard is <strong>deployed</strong>, it may <strong>maneuver</strong>.
            </span>
</template>
        </card>
        <div class="eliminated-overlay animated fade-in" v-if="eliminated">
            <fa-icon class="icon eliminated-icon" icon="ban" />
        </div>

        <div class="eliminated-overlay animated fade-in" v-if="eliminated">
            <fa-icon class="icon eliminated-icon" icon="ban" />
        </div>
		<card
            v-if="unitType === UNIT_TYPE_PIRATE"
			:concept-attribution="['Valentin Arnaud,', 'SpruceGoose']"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '1,5' },
                { type: 'opponent', position: '1,3' },
            ]"
			tactic-type="sea"
           	:colored-tiles="['0,0', '1,1', '2,0', '3,1', '0,5', '1,5', '2,5', '3,5']"
			:arrows="[
                { type: 'attack', position: '1,4', angle: '270' },
                { type: 'move', position: '1,5', angle: '32' },
                { type: 'move', position: '1,5', angle: '150' },
				{ type: 'move', position: '1,1', angle: '211' },
				{ type: 'move', position: '2,0', angle: '150' },
				{ type: 'move', position: '1,0', angle: '90' },
                { type: 'move', position: '2,5', angle: '211' },
				{ type: 'move', position: '1,1', angle: '328' },
            ]"
            :lines="[
                { type: 'attack', position: '1,3', angle: '90' },
            ]"
			card-footer-class="condensed"
        >
            <template v-slot:tactic>
<span >
                <strong>Attack</strong> a unit two spaces away in a straight line.
				 The intervening space cannot be occupied by a unit.
            </span>
</template>
            <template v-slot:description>
<span >
                The Pirate may <strong>move</strong> two spaces if it ends adjacent
				 to a Sea, and can move freely within and between the Seas.
            </span>
</template>
        </card>
		<card
            v-if="unitType === UNIT_TYPE_CORSAIR"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
			:concept-attribution="['Nikita Galushkin,', 'NoWaht, SpruceGoose']"
			tactic-length="medium-description"
            description-icon="exclamation-circle"
            :mini-units="[
                { type: 'self', position: '2,1' },
                { type: 'self', position: '2,1' },
                { type: 'opponent', position: '2,2' },
				{ type: 'self', position: '2,3' },
                { type: 'self', position: '2,3', icon: 'times' },
            ]"
           	:colored-tiles="[]"
			:arrows="[
				{ type: 'move', angle: '90', position: '2,2' },
				{ type: 'move', angle: '32', position: '2,3' },
			]"
            :lines="[{ type: 'move', angle: '90', position: '2,1' },]"
        >
            <template v-slot:tactic>
				<span>
					The Corsair may <strong>move</strong> over an adjacent unit in a straight line.
					Then, you may <strong>attack</strong> or discard a bolstered coin to <strong>move</strong>.
				</span>
			</template>
			<template v-slot:restriction>
				<span >
					The Corsair can only <strong>attack</strong> by using its <strong>tactic</strong>.
				</span>
			</template>
        </card>
		<div class="card-wrapper" v-if="unitType === UNIT_TYPE_LONGBOAT">
			<card
				v-if="unitType === UNIT_TYPE_LONGBOAT"
				:unit-type="unitType"
				:image-name="imageName"
				:quantity="quantity"
				:concept-attribution="['SpruceGoose,', 'NoWaht']"
				description-length="medium-description"
				description-icon="exclamation-circle"
				card-footer-class="condensed"
				:mini-units="[
					{ type: 'self', position: '2,1' },
					{ type: 'self', position: '2,1'},
				]"
				:colored-tiles="[]"
				:arrows="[
					{ type: 'move', angle: '32', position: '2,1' },
				]"
				:lines="[]"
			>
				<template v-slot:description>
					<span>
						A friendly unit may <strong>move</strong> or <strong>deploy</strong> onto the Longboat, and the Longboat may <strong>move</strong> under a friendly unit.
						When the Longboat <strong>moves</strong>, all stacked units <strong>move</strong> with it.
					</span>
				</template>
				<template v-slot:restriction>
					<span>
						The Longboat cannot be bolstered.
					</span>
				</template>
			</card>
		</div>
		<card
            v-if="unitType === UNIT_TYPE_ADMIRAL"
            :unit-type="unitType"
            :image-name="imageName"
            :quantity="quantity"
			:concept-attribution="['Samuel', '& Simon Besozzi']"
            description-icon="exclamation-circle"
            :mini-units="[
				{ type: 'self', position: '3,2'},
				{ type: 'opponent', position: '1,4' },
            ]"
			:colored-tiles="['1,4']"
			:arrows="[
				{ type: 'move', angle: '32', position: '3,2' },
			]"
            :lines="[]"
        >
            <template v-slot:description>
				<span>
					Whenever an opponent <strong>controls</strong>, the Admiral
					may <strong>maneuver</strong>.
				</span>
			</template>
        </card>
        <div class="eliminated-overlay animated fade-in" v-if="eliminated">
            <fa-icon class="icon eliminated-icon" icon="ban" />
        </div>
        <div class="eliminated-overlay animated fade-in" v-if="eliminated">
            <fa-icon class="icon eliminated-icon" icon="ban" />
        </div>
		<img
			v-if="unitType === UNIT_TYPE_COMMANDER"
			class="card-img"
			:src="getAssetUrl(`@/assets/cards/${UNIT_TYPE_COMMANDER.toLowerCase()}.png`)"
		/>
		<img
			v-if="unitType === UNIT_TYPE_DRAGOON"
			class="card-img"
			:src="getAssetUrl(`@/assets/cards/${UNIT_TYPE_DRAGOON.toLowerCase()}.png`)"
		/>
		<img
			v-if="unitType === UNIT_TYPE_MARKSMAN"
			class="card-img"
			:src="getAssetUrl(`@/assets/cards/${UNIT_TYPE_MARKSMAN.toLowerCase()}.png`)"
		/>
		<img
			v-if="unitType === UNIT_TYPE_RANGER"
			class="card-img"
			:src="getAssetUrl(`@/assets/cards/${UNIT_TYPE_RANGER.toLowerCase()}.png`)"
		/>
    </div>
</template>

<script>

import {
    UNIT_TYPE_ADMIRAL,
    UNIT_TYPE_ALCHEMIST,
    UNIT_TYPE_APPRENTICE,
    UNIT_TYPE_ARCHER,
    UNIT_TYPE_ASSASSIN,
    UNIT_TYPE_BANNERMAN,
    UNIT_TYPE_BERSERKER,
	UNIT_TYPE_BERSERKER_V2,
    UNIT_TYPE_BISHOP,
    UNIT_TYPE_SABOTEUR,
    UNIT_TYPE_CAVALRY,
    UNIT_TYPE_CROSSBOWMAN,
    UNIT_TYPE_EARL,
    UNIT_TYPE_EMISSARY,
	UNIT_TYPE_EMISSARY_V2,
    UNIT_TYPE_ENSIGN,
    UNIT_TYPE_FOOTMAN,
	UNIT_TYPE_FOOTMAN_V2,
    UNIT_TYPE_HEAVY_CAVALRY,
    UNIT_TYPE_HERALD,
    UNIT_TYPE_INFILTRATOR,
    UNIT_TYPE_KNIGHT,
    UNIT_TYPE_LANCER,
    UNIT_TYPE_LIGHT_CAVALRY,
	UNIT_TYPE_CORSAIR,
    UNIT_TYPE_OVERLORD,
    UNIT_TYPE_MARSHAL,
    UNIT_TYPE_MERCENARY,
    UNIT_TYPE_PIKEMAN,
	UNIT_TYPE_PIRATE,
    UNIT_TYPE_PITCH_THROWER,
    UNIT_TYPE_RAIDER,
    UNIT_TYPE_REARGUARD,
    UNIT_TYPE_ROYAL_GUARD,
    UNIT_TYPE_ROYAL_GUARD_NOBILITY,
    UNIT_TYPE_SAPPER,
    UNIT_TYPE_SCOUT,
    UNIT_TYPE_SIEGE_TOWER,
    UNIT_TYPE_SKIRMISHER,
    UNIT_TYPE_SWORDSMAN,
    UNIT_TYPE_TREBUCHET,
	UNIT_TYPE_TREBUCHET_V2,
	UNIT_TYPE_TREBUCHET_V3,
    UNIT_TYPE_WAR_DRUMMER,
    UNIT_TYPE_WAR_WAGON,
    UNIT_TYPE_WARLORD,
    UNIT_TYPE_WARRIOR_PRIEST,
	UNIT_TYPE_WARRIOR_PRIEST_V2,
    UNIT_TYPE_VANGUARD,
	UNIT_TYPE_LONGBOAT,
	UNIT_TYPE_COMMANDER,
	UNIT_TYPE_DRAGOON,
	UNIT_TYPE_MARKSMAN,
	UNIT_TYPE_RANGER,
} from '../Unit/Unit-types';
import { getAssetUrl } from '../../helpers/assets';

export default {
    props: ['imageName', 'unitType', 'quantity', 'eliminated'],
    name: 'UnitCard',
    data() {
        return {
            ['UNIT_TYPE_ADMIRAL']: UNIT_TYPE_ADMIRAL,
            ['UNIT_TYPE_ALCHEMIST']: UNIT_TYPE_ALCHEMIST,
            ['UNIT_TYPE_APPRENTICE']: UNIT_TYPE_APPRENTICE,
            ['UNIT_TYPE_ARCHER']: UNIT_TYPE_ARCHER,
            ['UNIT_TYPE_ASSASSIN']: UNIT_TYPE_ASSASSIN,
            ['UNIT_TYPE_BANNERMAN']: UNIT_TYPE_BANNERMAN,
            ['UNIT_TYPE_BERSERKER']: UNIT_TYPE_BERSERKER,
            ['UNIT_TYPE_BERSERKER_V2']: UNIT_TYPE_BERSERKER_V2,
            ['UNIT_TYPE_BISHOP']: UNIT_TYPE_BISHOP,
            ['UNIT_TYPE_SABOTEUR']: UNIT_TYPE_SABOTEUR,
            ['UNIT_TYPE_CAVALRY']: UNIT_TYPE_CAVALRY,
            ['UNIT_TYPE_COMMANDER']: UNIT_TYPE_COMMANDER,
            ['UNIT_TYPE_CROSSBOWMAN']: UNIT_TYPE_CROSSBOWMAN,
            ['UNIT_TYPE_DRAGOON']: UNIT_TYPE_DRAGOON,
            ['UNIT_TYPE_HERALD']: UNIT_TYPE_HERALD,
            ['UNIT_TYPE_HEAVY_CAVALRY']: UNIT_TYPE_HEAVY_CAVALRY,
            ['UNIT_TYPE_INFILTRATOR']: UNIT_TYPE_INFILTRATOR,
            ['UNIT_TYPE_EARL']: UNIT_TYPE_EARL,
            ['UNIT_TYPE_EMISSARY']: UNIT_TYPE_EMISSARY,
            ['UNIT_TYPE_EMISSARY_V2']: UNIT_TYPE_EMISSARY_V2,
            ['UNIT_TYPE_ENSIGN']: UNIT_TYPE_ENSIGN,
            ['UNIT_TYPE_FOOTMAN']: UNIT_TYPE_FOOTMAN,
            ['UNIT_TYPE_FOOTMAN_V2']: UNIT_TYPE_FOOTMAN_V2,
            ['UNIT_TYPE_KNIGHT']: UNIT_TYPE_KNIGHT,
            ['UNIT_TYPE_LANCER']: UNIT_TYPE_LANCER,
            ['UNIT_TYPE_LIGHT_CAVALRY']: UNIT_TYPE_LIGHT_CAVALRY,
            ['UNIT_TYPE_CORSAIR']: UNIT_TYPE_CORSAIR,
            ['UNIT_TYPE_OVERLORD']: UNIT_TYPE_OVERLORD,
            ['UNIT_TYPE_PITCH_THROWER']: UNIT_TYPE_PITCH_THROWER,
            ['UNIT_TYPE_MARKSMAN']: UNIT_TYPE_MARKSMAN,
            ['UNIT_TYPE_MARSHAL']: UNIT_TYPE_MARSHAL,
            ['UNIT_TYPE_MERCENARY']: UNIT_TYPE_MERCENARY,
            ['UNIT_TYPE_PIKEMAN']: UNIT_TYPE_PIKEMAN,
            ['UNIT_TYPE_PIRATE']: UNIT_TYPE_PIRATE,
            ['UNIT_TYPE_RAIDER']: UNIT_TYPE_RAIDER,
            ['UNIT_TYPE_RANGER']: UNIT_TYPE_RANGER,
            ['UNIT_TYPE_REARGUARD']: UNIT_TYPE_REARGUARD,
            ['UNIT_TYPE_ROYAL_GUARD']: UNIT_TYPE_ROYAL_GUARD,
            ['UNIT_TYPE_ROYAL_GUARD_NOBILITY']: UNIT_TYPE_ROYAL_GUARD_NOBILITY,
            ['UNIT_TYPE_SAPPER']: UNIT_TYPE_SAPPER,
            ['UNIT_TYPE_SCOUT']: UNIT_TYPE_SCOUT,
            ['UNIT_TYPE_SIEGE_TOWER']: UNIT_TYPE_SIEGE_TOWER,
            ['UNIT_TYPE_SKIRMISHER']: UNIT_TYPE_SKIRMISHER,
            ['UNIT_TYPE_SWORDSMAN']: UNIT_TYPE_SWORDSMAN,
            ['UNIT_TYPE_TREBUCHET']: UNIT_TYPE_TREBUCHET,
            ['UNIT_TYPE_TREBUCHET_V2']: UNIT_TYPE_TREBUCHET_V2,
            ['UNIT_TYPE_TREBUCHET_V3']: UNIT_TYPE_TREBUCHET_V3,
            ['UNIT_TYPE_VANGUARD']: UNIT_TYPE_VANGUARD,
            ['UNIT_TYPE_WAR_DRUMMER']: UNIT_TYPE_WAR_DRUMMER,
            ['UNIT_TYPE_WARLORD']: UNIT_TYPE_WARLORD,
            ['UNIT_TYPE_LONGBOAT']: UNIT_TYPE_LONGBOAT,
            ['UNIT_TYPE_WAR_WAGON']: UNIT_TYPE_WAR_WAGON,
            ['UNIT_TYPE_WARRIOR_PRIEST']: UNIT_TYPE_WARRIOR_PRIEST,
            ['UNIT_TYPE_WARRIOR_PRIEST_V2']: UNIT_TYPE_WARRIOR_PRIEST_V2,
        };
    },
	methods: {
		getAssetUrl,
	},
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .unit-card {
        position: relative;

        .eliminated-overlay {
            background: rgba(0,0,0,0.3);
            height: 100%;
            position: absolute;
            width: 100%;
            top: 0;
            z-index: 100;
            border-radius: 8px;

            .eliminated-icon {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                font-size: 6em;
                color: white;
                opacity: .2;
            }
        }

		.card-wrapper {

			.comment {
				background: white;
				border-radius: 4px;
				color: $lancer;
				padding: 4px;
				margin-top: 5px;
				font-size: .65rem;
			}
		}
    }

	.card-img {
        height: 270px;
		width: auto;
	}

</style>
